/**
 * feat-235 — the Tracks tab. Slice 1 of 8 of the curriculum-tracks program.
 *
 * A SEPARATE TAB, not a restructured tree (spec, human decision 2). D6 makes a
 * lesson belong to several tracks, so nesting lessons under tracks in
 * `MasterDetailTree` would render one lesson as several rows and every
 * selection, drag-reorder and expand/collapse would then need a rule for which
 * copy the author meant. `MasterDetailTree` keeps its single-parent assumption
 * because it keeps its single-parent data — this file does not import it.
 *
 * Same three-pane geometry as the Lessons tab (280px / 1fr / 380px, 480px floor,
 * `contain: size`) so the tab swap does not resize the card. The containment is
 * load-bearing rather than a performance tweak — see the long note on the
 * Lessons grid in `QuizContentManagement.tsx`; all three panes here are their own
 * scroll containers, which is exactly the condition that makes it safe.
 *
 * WRITE MODEL. Membership and prereqs are WHOLE-LIST replaces, so an add, a drag
 * and a remove are one request each with `sort_order` = array index.
 *
 * feat-243 — that is no longer last-write-wins. Every such write now also carries
 * the list it believes it is overwriting, and the server refuses a stale one with
 * a **409** naming what diverged and who changed it. The `conflict` state and the
 * banner below are that refusal made useful: refusing the write on its own only
 * converts silent data loss into lost work plus a confusing error.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  DefaultTrackState,
  LearnTrack,
  QuizUnit,
} from '../../../../services/quizContentAdminService';
import {
  deleteTrack,
  replaceTrackLessons,
  replaceTrackPrereqs,
  updateTrack,
} from '../../../../services/quizContentAdminService';
import { QUIZ_TERMS, termCount } from '../../quizTerminology';
import DefaultTrackAlert, { suppressesTrackCreation } from './DefaultTrackAlert';
import LessonLibraryPane, { LibraryFilter } from './LessonLibraryPane';
import TrackDetailPane, { TrackPatch, TrackSubtab } from './TrackDetailPane';
import TrackList from './TrackList';
import { unreachableLessonIds } from './trackGraph';
import TrackConflictBanner from './TrackConflictBanner';
import { deriveIntent, toTrackConflict } from './trackConflict';
import type { ConflictScope, TrackConflict } from './trackConflict';
import CurriculumMapView from './map/CurriculumMapView';
import { MAP_COPY, MAP_LABELS } from './map/mapCopy';

export interface TracksTabProps {
  tenantId: string;
  /** The tenant's whole Lesson library (level 1), from the curriculum payload. */
  units: QuizUnit[];
  tracks: LearnTrack[];
  unassignedLessonIds: string[];
  loading: boolean;
  /** Load error for the tracks payload only — the Lessons tab is unaffected. */
  error: string | null;
  onReload: () => Promise<void>;
  selectedTrackId: string | null;
  onSelectTrack: (trackId: string | null) => void;
  onCreateTrack: (title: string) => Promise<void>;
  glyphForKey: (key: string) => string;
  /** D2 health from the payload. Absent (an older backend) is treated as `ok` —
   *  a client that cannot ask must not accuse. */
  defaultTrackState?: DefaultTrackState;
  /** feat-242 — jump to the Lessons tab with this Lesson selected, the reverse
   *  of the jump `LessonTracksStrip`'s chips already perform. Used only by the
   *  Map. Optional so every existing call site compiles untouched. */
  onOpenLesson?: (lessonId: string) => void;
}

/** feat-242 — which view of this tab is showing. Assumption 1: Editor stays the
 *  default, Map is opt-in per visit, and the state lives HERE and is not
 *  persisted — there is no view state to lose. */
export type TracksView = 'editor' | 'map';

export const TracksTab: React.FC<TracksTabProps> = ({
  tenantId,
  units,
  tracks,
  unassignedLessonIds,
  loading,
  error,
  onReload,
  selectedTrackId,
  onSelectTrack,
  onCreateTrack,
  glyphForKey,
  defaultTrackState = 'ok',
  onOpenLesson,
}) => {
  const [trackSearch, setTrackSearch] = useState('');
  const [lessonSearch, setLessonSearch] = useState('');
  const [filter, setFilter] = useState<LibraryFilter>('all');
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [subtab, setSubtab] = useState<TrackSubtab>('lessons');
  const [view, setView] = useState<TracksView>('editor');
  const [busy, setBusy] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);
  /**
   * feat-243 — the refused write. **Its own state, not a reuse of `writeError`**,
   * because five things differ: it is structured rather than a string, it is
   * `attention-*` rather than `danger-*`, it carries a conditional action rather
   * than only Dismiss, it is set by the 409 branch rather than the catch, and it
   * RELOADS where `writeError` deliberately never does.
   *
   * The two are **mutually exclusive by construction**: the 409 branch sets this
   * and clears `writeError`, every other failure sets `writeError` and clears
   * this, and a success clears both. Two banners stacked in one slot describing
   * one failed write is a bug, not a thorough UI.
   */
  const [conflict, setConflict] = useState<TrackConflict | null>(null);
  /** THIS banner's replay is in flight — distinct from `busy`, so a drag
   *  elsewhere cannot relabel the button "Re-applying…". */
  const [reapplying, setReapplying] = useState(false);

  const selectedTrack = tracks.find((t) => t.id === selectedTrackId) ?? null;

  // Initialize / repair the selection when tracks load or the selected one goes.
  useEffect(() => {
    if (tracks.length === 0) return;
    if (selectedTrackId && tracks.some((t) => t.id === selectedTrackId)) return;
    onSelectTrack(tracks[0].id);
  }, [tracks, selectedTrackId, onSelectTrack]);

  // A pending selection belongs to the track it was going to be added to.
  useEffect(() => {
    setChecked(new Set());
  }, [selectedTrackId]);

  // feat-243 — a conflict is about ONE track. Carrying it to another one would
  // be a lie: the diff, the names and the replay all describe the track that was
  // selected when the write was refused.
  useEffect(() => {
    setConflict(null);
  }, [selectedTrackId]);

  const unitsById = useMemo(() => {
    const map: Record<string, QuizUnit> = {};
    for (const u of units) map[u.id] = u;
    return map;
  }, [units]);

  const trackCountByLesson = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of tracks) {
      for (const id of t.lesson_ids) counts[id] = (counts[id] ?? 0) + 1;
    }
    return counts;
  }, [tracks]);

  /** feat-242 — H1's population, derived ONCE here and handed to both consumers
   *  (the library footer's split, and the Map). Two derivations of "nobody can
   *  open this" that could disagree is the whole class of defect `trackGraph`
   *  exists to prevent. */
  const unreachable = useMemo(() => unreachableLessonIds(units, tracks), [units, tracks]);

  /** Every write goes through here: one busy flag, one error surface, one
   *  reload. A failed write must NOT leave the pane looking like it succeeded,
   *  which is also why this reports whether it worked — a rejected "Add 3" has
   *  to keep the three ticks so the author can retry rather than re-find them.
   *
   *  feat-243 — THE 409 IS THE ONE FAILURE THAT RELOADS. That is the opposite of
   *  the rule above, and it is why this is a structural change rather than a
   *  different message string: the server refused the write precisely because
   *  the pane is showing a list that is no longer true, so the author has to be
   *  handed the fresh one AND told why. Every other outcome is untouched —
   *  success reloads and reports true, every non-409 failure sets `writeError`,
   *  does not reload, and reports false (AC18).
   *
   *  `conflictCtx` is passed only by the two whole-list writes. Without it a 409
   *  is not even parsed — which is deliberate, because `DELETE /learn-tracks/{id}`
   *  on the default track is a live 409 that is NOT this feature's conflict and
   *  must keep landing in `writeError`. */
  const run = useCallback(
    async (
      fn: () => Promise<unknown>,
      conflictCtx?: { trackId: string; scope: ConflictScope; submitted: string[]; expected: string[] },
    ): Promise<boolean> => {
      setBusy(true);
      setWriteError(null);
      try {
        await fn();
        await onReload();
        setConflict(null);
        return true;
      } catch (e) {
        const refused = conflictCtx ? toTrackConflict(e, conflictCtx) : null;
        if (refused) {
          setConflict(refused);
          setWriteError(null);
          await onReload();
          return false;
        }
        setWriteError(e instanceof Error ? e.message : String(e));
        setConflict(null);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [onReload],
  );

  const patchTrack = useCallback(
    async (patch: TrackPatch) => {
      if (!selectedTrack) return;
      await run(() => updateTrack(selectedTrack.id, patch));
    },
    [run, selectedTrack],
  );

  const reorderTracks = useCallback(
    (orderedIds: string[]) => {
      // Same shape as the Lessons tab's `reorderUnits`: one PUT per row, 1-based.
      run(() => Promise.all(orderedIds.map((id, i) => updateTrack(id, { sort_order: i + 1 }))));
    },
    [run],
  );

  const setMembership = useCallback(
    async (orderedLessonIds: string[]): Promise<boolean> => {
      if (!selectedTrack) return false;
      // feat-243 / AC19 — the expected list is the one that was LOADED
      // (`selectedTrack.lesson_ids`, i.e. exactly what the pane is rendering),
      // never one recomputed from `orderedLessonIds`. A recomputed value always
      // matches, which makes the server's check vacuous.
      const expected = selectedTrack.lesson_ids;
      return run(() => replaceTrackLessons(selectedTrack.id, orderedLessonIds, expected), {
        trackId: selectedTrack.id,
        scope: 'lessons',
        submitted: orderedLessonIds,
        expected,
      });
    },
    [run, selectedTrack],
  );

  const addChecked = useCallback(async () => {
    if (!selectedTrack || checked.size === 0) return;
    // New members land at the END, in library order — the centre pane says so,
    // and a drag re-positions them after.
    const additions = units
      .map((u) => u.id)
      .filter((id) => checked.has(id) && !selectedTrack.lesson_ids.includes(id));
    if (additions.length === 0) return;
    const ok = await setMembership([...selectedTrack.lesson_ids, ...additions]);
    if (ok) setChecked(new Set());
  }, [checked, selectedTrack, setMembership, units]);

  const removeLesson = useCallback(
    (lessonId: string) => {
      if (!selectedTrack) return;
      setMembership(selectedTrack.lesson_ids.filter((id) => id !== lessonId));
    },
    [selectedTrack, setMembership],
  );

  const setPrereqs = useCallback(
    async (requiredTrackIds: string[]): Promise<boolean> => {
      if (!selectedTrack) return false;
      // feat-243 / AC19 — as `setMembership`: the LOADED prereq list, not one
      // derived from `requiredTrackIds`.
      const expected = selectedTrack.prereq_track_ids;
      return run(() => replaceTrackPrereqs(selectedTrack.id, requiredTrackIds, expected), {
        trackId: selectedTrack.id,
        scope: 'prereqs',
        submitted: requiredTrackIds,
        expected,
      });
    },
    [run, selectedTrack],
  );

  /**
   * feat-243 — REPLAY THE STATED INTENT, on an explicit press, against the list
   * that came back from the refetch.
   *
   * This is not the silent merge R2 forbids. The intent was derived and SHOWN —
   * the line beside the button says what this will do — and nothing re-PUTs
   * until the author presses. The `expected` list this sends is once again the
   * loaded one, so a second conflict racing in behind the first is refused too
   * rather than clobbering it.
   */
  const reapplyConflict = useCallback(async () => {
    if (!conflict || !selectedTrack || conflict.trackId !== selectedTrack.id) return;
    const intent = deriveIntent(conflict);
    if (intent.suppressed) return;
    setReapplying(true);
    try {
      if (conflict.scope === 'lessons') await setMembership(intent.replay);
      else await setPrereqs(intent.replay);
    } finally {
      setReapplying(false);
    }
  }, [conflict, selectedTrack, setMembership, setPrereqs]);

  const removeTrack = useCallback(() => {
    if (!selectedTrack || selectedTrack.is_default) return;
    // Say what SURVIVES. The membership rows cascade; the lessons do not — and
    // "this can't be undone" is literally true here, because revert is off for
    // tracks (spec decision 5).
    const message =
      `Delete ${QUIZ_TERMS.track.singular} “${selectedTrack.title || '(untitled)'}”? ` +
      `Its ${termCount(selectedTrack.lesson_ids.length, 'lesson')} stay in your library and keep any ` +
      `other ${QUIZ_TERMS.track.singular} they're in. This can't be undone.`;
    if (!window.confirm(message)) return;
    const goneId = selectedTrack.id;
    run(async () => {
      await deleteTrack(goneId);
      onSelectTrack(null);
    });
  }, [onSelectTrack, run, selectedTrack]);

  const activeCount = tracks.filter((t) => t.active).length;
  const d2Broken = defaultTrackState !== 'ok';
  // Suppressing creation is the load-bearing half of the D2 alert: a banner on
  // its own leaves the evidence-destroying action as the most prominent control
  // on an otherwise empty screen. Only `missing_no_tracks` suppresses — from the
  // other two states there is no laundering path, and blocking authoring there
  // would be a worse bug than the one being reported.
  const canCreate = !suppressesTrackCreation(defaultTrackState);

  /** feat-243 — the pending intent, DERIVED from what was sent versus what was
   *  loaded. No `kind` prop is threaded through the panes: the reorder rule is a
   *  property of the data, not a string three call sites have to keep truthful. */
  const conflictIntent = useMemo(() => (conflict ? deriveIntent(conflict) : null), [conflict]);

  /** An id in the 409 that resolves to nothing returns null, and the chip falls
   *  back to the raw id in mono. That is reachable the moment someone is also
   *  editing the Lessons tab — never render a blank chip, and never `undefined`. */
  const conflictTitleOf = useCallback(
    (id: string): string | null => {
      if (conflict?.scope === 'prereqs') return tracks.find((t) => t.id === id)?.title || null;
      return unitsById[id]?.title || null;
    },
    [conflict, tracks, unitsById],
  );

  /**
   * feat-242 — THE VIEW BAR. Q2: `spec.md` §1's ASCII drew this toggle in the
   * card header, but Assumption 1 puts its state in `TracksTab`. Those are two
   * different containers — the header is `QuizContentManagement.tsx`, and it
   * already carries the `Lessons | Tracks` tablist AND the `+ New Track` primary
   * button. A second segmented control on that strip would put two tab-shaped
   * things side by side that switch different axes.
   *
   * So it lives here, directly under the tab strip and above the pane, with a
   * quieter treatment than the coral tab underline so the two are never mistaken
   * for one another. That keeps Assumption 1 literally true,
   * `QuizContentManagement.tsx` needs no change for it at all, and it stays off
   * the header strip `feat-241` is adding a fourth tab to.
   */
  const viewBar = (
    <div
      data-testid="tracks-view-bar"
      className="flex items-center justify-between gap-3 px-6 py-2.5 border-b border-softBorder bg-cream/50 shrink-0"
    >
      <div className="flex items-center gap-2 text-[12px] text-inkMuted min-w-0">
        {view === 'map' && (
          <>
            <span className="shrink-0 font-bold text-ink">{MAP_LABELS.map}</span>
            <span className="truncate">{MAP_COPY.viewBarBlurb()}</span>
          </>
        )}
      </div>
      <div
        className="shrink-0 flex items-center gap-0.5 bg-cream-dark rounded-full p-0.5"
        role="tablist"
        aria-label="View"
      >
        {(['editor', 'map'] as const).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={view === key}
            data-testid={`tracks-view-${key}`}
            onClick={() => setView(key)}
            className={`text-[12px] font-bold rounded-full px-3 py-1 ${
              view === key ? 'bg-white text-ink shadow-sm' : 'text-inkMuted hover:text-ink'
            }`}
          >
            {key === 'editor' ? MAP_LABELS.editor : MAP_LABELS.map}
          </button>
        ))}
      </div>
    </div>
  );

  const grid = (
    <div
      className="grid flex-1"
      style={{
        gridTemplateColumns: '280px minmax(0,1fr) 380px',
        gridTemplateRows: 'minmax(0, 1fr)',
        minHeight: 480,
        contain: 'size',
      }}
    >
      <TrackList
        tracks={tracks}
        selectedTrackId={selectedTrackId}
        onSelectTrack={onSelectTrack}
        search={trackSearch}
        onSearch={setTrackSearch}
        onCreateTrack={onCreateTrack}
        onReorderTracks={reorderTracks}
        canCreate={canCreate}
      />

      {selectedTrack ? (
        <TrackDetailPane
          tenantId={tenantId}
          track={selectedTrack}
          tracks={tracks}
          unitsById={unitsById}
          glyphForKey={glyphForKey}
          subtab={subtab}
          onSubtab={setSubtab}
          onPatch={patchTrack}
          onDelete={removeTrack}
          onReorderLessons={setMembership}
          onRemoveLesson={removeLesson}
          onSetPrereqs={setPrereqs}
          busy={busy}
        />
      ) : (
        <section className="min-h-0 overflow-y-auto flex items-center justify-center p-6 text-sm text-inkMuted">
          Select a {QUIZ_TERMS.track.singular} on the left, or make one.
        </section>
      )}

      <LessonLibraryPane
        units={units}
        track={selectedTrack}
        unassignedLessonIds={unassignedLessonIds}
        trackCountByLesson={trackCountByLesson}
        unreachableLessonIds={unreachable}
        checked={checked}
        onToggle={(id) =>
          setChecked((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          })
        }
        onClear={() => setChecked(new Set())}
        onCommitAdd={addChecked}
        search={lessonSearch}
        onSearch={setLessonSearch}
        filter={filter}
        onFilter={setFilter}
        busy={busy}
      />
    </div>
  );

  if (loading && tracks.length === 0 && !error) {
    return (
      <div
        className="grid flex-1"
        style={{
          gridTemplateColumns: '280px minmax(0,1fr) 380px',
          gridTemplateRows: 'minmax(0, 1fr)',
          minHeight: 480,
          contain: 'size',
        }}
      >
        <div className="border-r border-softBorder p-3 space-y-2.5">
          <div className="h-9 bg-gray-100 rounded-full animate-pulse" />
          <div className="h-7 bg-gray-100 rounded-lg animate-pulse" />
          <div className="h-7 bg-gray-100 rounded-lg animate-pulse w-5/6" />
        </div>
        <div className="p-5 space-y-3">
          <div className="h-5 bg-gray-100 rounded animate-pulse w-1/3" />
          <div className="h-12 bg-gray-100 rounded-2xl animate-pulse" />
          <div className="h-12 bg-gray-100 rounded-2xl animate-pulse w-11/12" />
        </div>
        <div className="border-l border-softBorder p-5 space-y-3">
          <div className="h-6 bg-gray-100 rounded animate-pulse w-1/2" />
          <div className="h-16 bg-gray-100 rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (error && tracks.length === 0) {
    return (
      <div className="p-6 flex-1 min-h-0 overflow-y-auto">
        <div className="bg-danger-surface border border-danger-line/40 text-danger-text rounded-2xl px-5 py-4 flex items-center gap-4">
          <span className="text-2xl">⚠️</span>
          <div className="flex-1">
            <h4 className="font-display font-semibold">
              Couldn't load {QUIZ_TERMS.track.plural}
            </h4>
            <p className="text-sm opacity-90">
              The request to <code className="bg-white/60 rounded px-1">/api/tenant/…/learn-tracks</code>{' '}
              failed. The {QUIZ_TERMS.lesson.plural} tab is unaffected.
            </p>
          </div>
          <button
            onClick={() => {
              onReload();
            }}
            className="bg-danger-text text-white font-display font-semibold rounded-full px-4 py-2 text-sm hover:brightness-110 shrink-0"
          >
            ↻ Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* feat-243 — ONE SLOT, and the conflict wins it. The two are already
          mutually exclusive by construction (see the `conflict` state); this
          ordering is what makes that visible at the render site rather than only
          in the setters. */}
      {conflict && conflictIntent && (
        <TrackConflictBanner
          conflict={conflict}
          intent={conflictIntent}
          titleOf={conflictTitleOf}
          busy={busy}
          reapplying={reapplying}
          onReapply={reapplyConflict}
          onDismiss={() => setConflict(null)}
        />
      )}
      {!conflict && writeError && (
        <div className="mx-5 mt-4 shrink-0 bg-danger-surface border border-danger-line/40 text-danger-text rounded-xl px-4 py-2 text-sm flex items-center justify-between gap-3">
          <span>{writeError}</span>
          <button onClick={() => setWriteError(null)} className="font-semibold hover:underline shrink-0">
            Dismiss
          </button>
        </div>
      )}
      {error && tracks.length > 0 && (
        <div className="mx-5 mt-4 shrink-0 bg-danger-surface border border-danger-line/40 text-danger-text rounded-xl px-4 py-2 text-sm flex items-center justify-between gap-3">
          <span>Refresh failed — showing the last loaded {QUIZ_TERMS.track.plural}.</span>
          <button
            onClick={() => {
              onReload();
            }}
            className="font-semibold hover:underline shrink-0"
          >
            ↻ Retry
          </button>
        </div>
      )}
      {/* A broken tenant is not a day-one tenant, so these two are mutually
          exclusive: the D2 alert wins and the reassuring banner is suppressed. */}
      {!loading && d2Broken && (
        <DefaultTrackAlert
          state={defaultTrackState}
          tenantId={tenantId}
          trackCount={tracks.length}
        />
      )}

      {/* The safety message every existing tenant opens to. Invariant 1 says
          Ladle & Leaf notices nothing, but their author still opens this tab and
          sees a new concept on top of their content; this answers "what did this
          change for my crew?" before it is asked, and states the D3 rule that
          makes it true.

          THE PREDICATE AND THE COPY ARE NOT THE SAME QUESTION. D3's rule is
          about ACTIVE tracks, so `activeCount <= 1` is the right trigger — but
          "all in one Track … until you add a second one" is only true when there
          IS only one track. Seven tracks with six hidden also satisfies the
          predicate, and there both clauses are false: the Lessons are spread
          across seven, and the remedy is to unhide, not to add. That state is
          reachable during authoring precisely because Hidden is the designed-safe
          alternative to Delete, so the copy splits. */}
      {!loading && !d2Broken && activeCount <= 1 && tracks.length > 0 && (
        <div
          data-testid="day-one-banner"
          className="mx-6 mt-4 shrink-0 rounded-xl border border-preview-border bg-preview-light text-ink/85 text-[12.5px] px-3.5 py-2.5"
        >
          {tracks.length === 1 ? (
            <>
              Your {QUIZ_TERMS.lesson.plural} are all in one {QUIZ_TERMS.track.singular}, so the
              crew's app looks exactly as it did — the {QUIZ_TERMS.track.plural} screen stays
              hidden until you add a second one.
            </>
          ) : activeCount === 1 ? (
            <>
              Only 1 of your {tracks.length} {QUIZ_TERMS.track.plural} is visible to the crew;
              the other {tracks.length - 1} are hidden. The {QUIZ_TERMS.track.plural} screen
              stays hidden until a second one is visible — unhide one rather than adding
              another.
            </>
          ) : (
            <>
              None of your {tracks.length} {QUIZ_TERMS.track.plural} are visible to the crew —
              every one is hidden. Unhide the ones the crew should see; adding another won't
              change what they get.
            </>
          )}
        </div>
      )}
      {viewBar}
      {view === 'map' ? (
        <CurriculumMapView
          tracks={tracks}
          units={units}
          unassignedLessonIds={unassignedLessonIds}
          // Clicking a Track node returns you to the Editor with it selected.
          // That is a NAVIGATION, and it is the whole of what a Track click does
          // — no write originates from the Map (AC18).
          onOpenTrack={(trackId) => {
            onSelectTrack(trackId);
            setView('editor');
          }}
          onOpenLesson={onOpenLesson}
        />
      ) : (
        grid
      )}
    </>
  );
};

export default TracksTab;
